import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowUp, ArrowDown, Plus, Pencil, Trash2, Upload, Video, Link2, Eye, Loader2 } from "lucide-react";
import { api, errMsg, mediaUrl } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { ACard, AField, ABtn } from "./ui";
import { LessonDialog } from "./LessonDialog";

const swap = (arr, i, j) => { const a = [...arr]; [a[i], a[j]] = [a[j], a[i]]; return a; };

const CourseForm = ({ course, onSaved }) => {
  const [f, setF] = useState(() => ({ ...course, what_you_learn: (course.what_you_learn || []).join("\n"), requirements: (course.requirements || []).join("\n") }));
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const uploadCover = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/admin/upload-image", fd); setF((p) => ({ ...p, cover_image: data.url })); toast.success("تم رفع الصورة"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const save = async () => {
    setBusy(true);
    const lines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);
    const body = { title: f.title, slug: f.slug, subtitle: f.subtitle, description: f.description, cover_image: f.cover_image,
      price: Number(f.price) || 0, old_price: Number(f.old_price) || 0, level: f.level, duration_text: f.duration_text, category: f.category,
      what_you_learn: lines(f.what_you_learn), requirements: lines(f.requirements), published: f.published, featured: f.featured, order: Number(f.order) || 0 };
    try { const { data } = await api.put(`/admin/courses/${course.id}`, body); setF((p) => ({ ...p, slug: data.slug })); toast.success("تم حفظ الكورس"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <ACard className="space-y-4" data-testid="course-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <AField label="عنوان الكورس" value={f.title} onChange={set("title")} testid="course-title-input" className="sm:col-span-2" />
        <AField label="الرابط المختصر (slug)" dir="ltr" value={f.slug} onChange={set("slug")} testid="course-slug-input" hint="يظهر في رابط صفحة الكورس" />
        <AField label="التصنيف" value={f.category} onChange={set("category")} testid="course-category-input" />
        <AField label="وصف مختصر" value={f.subtitle} onChange={set("subtitle")} testid="course-subtitle-input" className="sm:col-span-2" />
        <AField label="الوصف التفصيلي" textarea value={f.description} onChange={set("description")} testid="course-description-input" className="sm:col-span-2" />
        <AField label="السعر (ج.م)" type="number" min="0" value={f.price} onChange={set("price")} testid="course-price-input" />
        <AField label="السعر قبل الخصم (اختياري)" type="number" min="0" value={f.old_price} onChange={set("old_price")} testid="course-old-price-input" />
        <AField label="المستوى" value={f.level} onChange={set("level")} testid="course-level-input" />
        <AField label="مدة الكورس" value={f.duration_text} onChange={set("duration_text")} testid="course-duration-input" />
        <AField label="هتتعلم إيه (كل نقطة في سطر)" textarea value={f.what_you_learn} onChange={set("what_you_learn")} testid="course-learn-input" />
        <AField label="المتطلبات (كل نقطة في سطر)" textarea value={f.requirements} onChange={set("requirements")} testid="course-req-input" />
        <div className="sm:col-span-2 space-y-2">
          <AField label="صورة الغلاف (رابط)" dir="ltr" value={f.cover_image} onChange={set("cover_image")} testid="course-cover-input" />
          <div className="flex items-center gap-3">
            {f.cover_image && <img src={mediaUrl(f.cover_image)} alt="" className="w-28 aspect-video object-cover rounded-lg" />}
            <label className="cursor-pointer inline-flex items-center gap-2 text-sm px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10"><Upload size={15} />رفع صورة
              <input type="file" hidden accept="image/jpeg,image/png,image/webp" onChange={(e) => uploadCover(e.target.files[0])} data-testid="course-cover-file-input" />
            </label>
          </div>
        </div>
        <AField label="ترتيب العرض" type="number" value={f.order} onChange={set("order")} testid="course-order-input" />
        <div className="flex items-center gap-6 pt-5">
          <label className="flex items-center gap-2 text-sm"><Switch checked={!!f.published} onCheckedChange={(v) => setF({ ...f, published: v })} data-testid="course-published-switch" />منشور</label>
          <label className="flex items-center gap-2 text-sm"><Switch checked={!!f.featured} onCheckedChange={(v) => setF({ ...f, featured: v })} data-testid="course-featured-switch" />مميز</label>
        </div>
      </div>
      <div className="flex justify-end"><ABtn onClick={save} disabled={busy} data-testid="course-save-btn">{busy && <Loader2 size={15} className="animate-spin" />}حفظ بيانات الكورس</ABtn></div>
    </ACard>
  );
};

const VideoIcon = ({ l }) => l.video_type === "upload" && l.video_path ? <Video size={15} className="text-emerald-400" /> : l.video_type === "external" ? <Link2 size={15} className="text-sky-400" /> : <Video size={15} className="text-slate-600" />;

const ModuleBlock = ({ m, mi, total, onMove, reload, openLesson }) => {
  const rename = async () => {
    const t = window.prompt("اسم الوحدة", m.title);
    if (t) { await api.put(`/admin/modules/${m.id}`, { title: t }); reload(); }
  };
  const del = async () => { if (window.confirm("حذف الوحدة وكل دروسها؟")) { await api.delete(`/admin/modules/${m.id}`); reload(); } };
  const moveLesson = async (i, d) => {
    const ids = swap(m.lessons, i, i + d).map((l) => l.id);
    await api.post(`/admin/modules/${m.id}/lessons/reorder`, { ids }); reload();
  };
  const delLesson = async (l) => { if (window.confirm(`حذف "${l.title}"؟`)) { await api.delete(`/admin/lessons/${l.id}`); reload(); } };
  return (
    <ACard className="space-y-3" data-testid={`module-${m.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-semibold">الوحدة {mi + 1}: {m.title}</p>
        <div className="flex gap-1">
          <ABtn variant="ghost" disabled={mi === 0} onClick={() => onMove(mi, -1)} aria-label="لأعلى"><ArrowUp size={14} /></ABtn>
          <ABtn variant="ghost" disabled={mi === total - 1} onClick={() => onMove(mi, 1)} aria-label="لأسفل"><ArrowDown size={14} /></ABtn>
          <ABtn variant="ghost" onClick={rename} data-testid={`rename-module-${m.id}`}><Pencil size={14} /></ABtn>
          <ABtn variant="danger" onClick={del} data-testid={`delete-module-${m.id}`}><Trash2 size={14} /></ABtn>
        </div>
      </div>
      <ul className="space-y-1">
        {m.lessons.map((l, i) => (
          <li key={l.id} className="flex items-center gap-3 rounded-xl bg-white/[0.03] px-3 py-2" data-testid={`admin-lesson-${l.id}`}>
            <VideoIcon l={l} />
            <span className="flex-1 text-sm truncate">{l.title}</span>
            {l.is_preview && <Eye size={14} className="text-sky-400" aria-label="معاينة" />}
            <button disabled={i === 0} onClick={() => moveLesson(i, -1)} className="text-slate-500 hover:text-white disabled:opacity-20"><ArrowUp size={14} /></button>
            <button disabled={i === m.lessons.length - 1} onClick={() => moveLesson(i, 1)} className="text-slate-500 hover:text-white disabled:opacity-20"><ArrowDown size={14} /></button>
            <button onClick={() => openLesson(m.id, l)} data-testid={`edit-lesson-${l.id}`} className="text-slate-400 hover:text-white"><Pencil size={14} /></button>
            <button onClick={() => delLesson(l)} data-testid={`delete-lesson-${l.id}`} className="text-red-400/70 hover:text-red-300"><Trash2 size={14} /></button>
          </li>
        ))}
      </ul>
      <ABtn variant="ghost" onClick={() => openLesson(m.id, null)} data-testid={`add-lesson-${m.id}`}><Plus size={14} />إضافة درس</ABtn>
    </ACard>
  );
};

export default function AdminCourseEditor() {
  const { id } = useParams();
  const [course, setCourse] = useState(null);
  const [dlg, setDlg] = useState({ open: false });
  const reload = useCallback(() => api.get(`/admin/courses/${id}`).then((r) => setCourse(r.data)), [id]);
  useEffect(() => { reload(); }, [reload]);
  if (!course) return <Loader2 className="animate-spin" />;

  const addModule = async () => {
    const t = window.prompt("اسم الوحدة الجديدة");
    if (t) { await api.post(`/admin/courses/${id}/modules`, { title: t }); reload(); }
  };
  const moveModule = async (i, d) => {
    await api.post(`/admin/courses/${id}/modules/reorder`, { ids: swap(course.modules, i, i + d).map((m) => m.id) }); reload();
  };

  return (
    <div className="space-y-8" data-testid="admin-course-editor">
      <div className="flex items-center gap-3">
        <Link to="/admin/courses" className="text-slate-400 hover:text-white"><ArrowRight /></Link>
        <h1 className="font-heading text-2xl font-bold truncate">{course.title}</h1>
      </div>
      <CourseForm key={course.id} course={course} onSaved={reload} />
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-xl font-bold">المنهج ({course.lessons_count} درس)</h2>
          <ABtn onClick={addModule} data-testid="add-module-btn"><Plus size={15} />وحدة جديدة</ABtn>
        </div>
        {course.modules.map((m, mi) => (
          <ModuleBlock key={m.id} m={m} mi={mi} total={course.modules.length} onMove={moveModule} reload={reload}
            openLesson={(moduleId, lesson) => setDlg({ open: true, moduleId, lesson })} />
        ))}
      </section>
      <LessonDialog open={dlg.open} onClose={() => setDlg({ open: false })} courseId={id} moduleId={dlg.moduleId} lesson={dlg.lesson} onSaved={reload} />
    </div>
  );
}
